import React, { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { authGet, authPost } from "../../../lib";

const useStyles = makeStyles((theme) => ({
  fields: { minWidth: 560, paddingTop: theme.spacing(0.5) },
  row: { display: "flex", gap: theme.spacing(1.5), marginBottom: theme.spacing(1.5) },
  /** Unmissable: the body is HTML and goes to a customer. */
  guard: {
    padding: theme.spacing(1.25, 1.5),
    marginBottom: theme.spacing(1.5),
    borderRadius: 3,
    fontSize: 12.5,
    lineHeight: 1.55,
  },
  blocked: { background: "rgba(97,97,97,0.10)", color: "#424242", border: "1px solid rgba(0,0,0,0.12)" },
  live: { background: "rgba(183,28,28,0.08)", color: "#b71c1c", border: "1px solid rgba(183,28,28,0.35)" },
  redirect: { background: "rgba(224,168,0,0.14)", color: "#8a6100", border: "1px solid rgba(224,168,0,0.5)" },
  preview: {
    border: "1px solid rgba(0,0,0,0.12)",
    borderRadius: 3,
    padding: theme.spacing(1.5),
    maxHeight: 260,
    overflowY: "auto",
    fontSize: 13,
    background: "#fff",
  },
  error: { color: "#c62828", fontSize: 13, marginTop: theme.spacing(1) },
  ok: { color: "#1b5e20", fontSize: 13, marginTop: theme.spacing(1) },
}));

const TITLES = {
  return: "Return Notice",
  cancel: "Cancellation Notice",
  contact: "Contact Request",
  resend: "Resend Order Confirmation",
};

/**
 * Composes an order action email, shows it, and sends it only on a second,
 * deliberate click — the legacy page previewed these too.
 *
 * The dialog states plainly what will happen before you press Send: whether
 * sending is on at all, and whether the mail is being redirected away from the
 * customer.
 */
export function OrderActionDialog({ orderId, action, onClose, onSent }) {
  const classes = useStyles();

  const [state, setState] = useState(null);
  const [form, setForm] = useState({ to: "", subject: "", body: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState("");

  useEffect(() => {
    if (!action) return;
    let cancelled = false;
    setState(null);
    setError("");
    setDone("");

    authGet(`/orders/${orderId}/actions/${action}`).then(({ data, error: err }) => {
      if (cancelled) return;
      if (err) {
        setError(err.message || "Could not compose this email.");
        setState({ sending: {} });
        return;
      }
      setState(data);
      setForm({ to: data.to ?? "", subject: data.subject ?? "", body: data.body ?? "" });
    });

    return () => {
      cancelled = true;
    };
  }, [orderId, action]);

  if (!action) return null;

  const sending = state?.sending ?? {};
  const blocked = sending.blocked;

  const send = async () => {
    setBusy(true);
    const { data, error: err } = await authPost(`/orders/${orderId}/actions/${action}`, form);
    setBusy(false);

    if (err) {
      setError(err.message || "Could not send.");
      return;
    }
    if (data?.sent === false) {
      // Expected on a dev machine; the guard already explained why.
      setError(data.blocked || "Sending is disabled.");
      return;
    }
    setDone(`Sent to ${data.to}. Logged as "${data.logged}".`);
    onSent();
  };

  return (
    <Dialog open onClose={busy ? undefined : onClose} maxWidth="md">
      <DialogTitle>{TITLES[action] ?? action}</DialogTitle>
      <DialogContent>
        <div className={classes.fields}>
          {!state ? (
            "Composing…"
          ) : (
            <>
              {blocked ? (
                <div className={`${classes.guard} ${classes.blocked}`}>
                  <strong>Nothing will be sent.</strong> {blocked}
                </div>
              ) : sending.redirectTo ? (
                <div className={`${classes.guard} ${classes.redirect}`}>
                  <strong>Redirected.</strong> This will go to{" "}
                  <strong>{sending.redirectTo}</strong>, not to the customer.
                </div>
              ) : (
                <div className={`${classes.guard} ${classes.live}`}>
                  <strong>This sends to the real customer.</strong> Check the address
                  below before pressing Send.
                </div>
              )}

              <div className={classes.row}>
                <TextField
                  label="To"
                  style={{ flex: "1 1 260px" }}
                  value={form.to}
                  onChange={(e) => setForm((s) => ({ ...s, to: e.target.value }))}
                />
                <TextField
                  label="Subject"
                  style={{ flex: "2 1 320px" }}
                  value={form.subject}
                  onChange={(e) => setForm((s) => ({ ...s, subject: e.target.value }))}
                />
              </div>

              <TextField
                label="Body (HTML)"
                multiline
                rows={8}
                fullWidth
                variant="outlined"
                value={form.body}
                onChange={(e) => setForm((s) => ({ ...s, body: e.target.value }))}
              />

              <div style={{ fontSize: 11.5, margin: "10px 0 4px", opacity: 0.6 }}>
                PREVIEW
              </div>
              {/* The body is admin-authored HTML shown back to its author, not
                  visitor content — the same trust boundary as the email itself. */}
              <div
                className={classes.preview}
                dangerouslySetInnerHTML={{ __html: form.body }}
              />
            </>
          )}

          {error && <div className={classes.error}>{error}</div>}
          {done && <div className={classes.ok}>{done}</div>}
        </div>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={busy} size="small">
          {done ? "Close" : "Cancel"}
        </Button>
        <Button
          onClick={send}
          disabled={busy || !state || !!blocked || !!done}
          size="small"
          variant="contained"
          color="primary"
        >
          {busy ? "Sending…" : "Send"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default OrderActionDialog;
